import React from 'react';
import ShareComponent from './ShareComponent';
import { PromptVersion } from '@/hooks/usePrompt';
import { useLanguage } from '@/contexts';

interface PromptHeaderProps {
  title: string;
  isNewPrompt: boolean;
  latestVersion?: PromptVersion | null;
  promptId: string;
  showShare: boolean;
  onToggleShare: () => void;
  onCopyLink: () => void;
}

// Simple title component
const PromptTitle: React.FC<{ 
  title: string; 
  isNewPrompt: boolean; 
  latestVersion?: PromptVersion | null; 
}> = ({ title, isNewPrompt, latestVersion }) => {
  const { t } = useLanguage();
  
  return (
    <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-brand-dark-blue dark:text-slate-100 gap-2 flex flex-col sm:flex-row sm:items-center">
      <span className="break-words">{title}</span>
      <div className="flex flex-wrap items-center gap-2">
        {!isNewPrompt && <span className="text-xs sm:text-sm font-normal text-gray-500 dark:text-slate-400">({t("userGenerated")})</span>}
        {latestVersion && (
          <span className="text-xs sm:text-sm font-normal text-blue-600 dark:text-blue-400">
            - {t("latestVersion")} {latestVersion.version_number}
          </span>
        )}
      </div>
    </h1>
  );
};

// Main header component that composes title and share components
const PromptHeader: React.FC<PromptHeaderProps> = ({
  title,
  isNewPrompt,
  latestVersion,
  promptId,
  showShare,
  onToggleShare,
  onCopyLink
}) => (
  <div className="px-3 sm:px-6 py-3 sm:py-4">
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
      <PromptTitle 
        title={title} 
        isNewPrompt={isNewPrompt} 
        latestVersion={latestVersion} 
      />
      <ShareComponent 
        promptId={promptId}
        isNewPrompt={isNewPrompt}
        isOpen={showShare}
        onToggle={onToggleShare}
        onCopy={onCopyLink}
      />
    </div>
  </div>
);

export default PromptHeader;

// Export individual components for reuse
export { PromptTitle };
